import React, { useContext } from 'react'
import axios from 'axios'

const ToolsContext = React.createContext();

export function useTools() {
    return useContext(ToolsContext);
}

const ToolProvider = ({ children, editor, historyInfo }) => {
    const tools = {
        onAddTriangle: () => editor?.addTriangle(123),
        onAddCircle: () => editor?.addCircle(),
        onAddRectangle: () => editor?.addRectangle(),
        onAddPentagon: () => editor?.addPolygonShape(5, 'Pentágono'),
        onAddHexagon: () => editor?.addPolygonShape(6, 'Hexágono'),
        onAddOctagon: () => editor?.addPolygonShape(8, 'Octógono'),
        onAddStar: () => editor?.addStar(),
        onAddDiamond: () => editor?.addDiamond(),
        onAddText: (text, font, style) => editor?.addText(text, font, style),
        onUpdateText: (text) => editor?.updateText(text),
        onSetFontText: (font) => editor?.setFontFamily(font),
        onSetTextStyle: (style) => editor?.setTextStyle(style),
        onPreviewText: (draft) => editor?.previewText(draft),
        onRestoreTextPreview: (draft) => editor?.restoreTextPreview(draft),
        onDeleteSelectedObject: () => editor?.deleteSelected(),
        onDeleteAll: () => editor?.deleteAll(),
        onCopy: () => editor?.copy2(),
        onPaste: () => editor?.paste2(),
        onChangeColor: (color) => editor?.setColor(color.value),
        onSetHoleOperation: () => editor?.setHoleOperation(),
        // Esquinas redondeadas de los rectángulos seleccionados (en mm).
        getRectRadius: () => editor?.getRectRadius() ?? null,
        onChangeRectRadius: (radiusMm) => editor?.setRectRadius(radiusMm),
        onChangeColorBaseLayout: (color) => editor?.setColorBaseLayout(color.value),
        onHoleActivation: (activation) => editor?.setHole(activation),
        // Lightbox hanging slots. A marker only sets a position; the slot's
        // dimensions come from the 3D parameters. There can be several.
        onAddKeyhole: (options) => editor?.addKeyhole(options),
        onRemoveKeyhole: (keyholeId) => editor?.removeKeyhole(keyholeId),
        onResizeKeyhole: (keyholeId, size) => editor?.resizeKeyhole(keyholeId, size),
        getKeyholes: () => editor?.getKeyholes() ?? [],
        // Avellanado pasante: el otro sistema de colgado. Mismo patrón que la
        // ranura — el marcador pone el sitio y su id lleva las dos cotas.
        onAddScrew: (options) => editor?.addScrew(options),
        onRemoveScrew: (screwId) => editor?.removeScrew(screwId),
        onResizeScrew: (screwId, size) => editor?.resizeScrew(screwId, size),
        getScrews: () => editor?.getScrews() ?? [],
        // Perfil de base y guía de la servilleta (servilletero de paredes).
        getNapkinJoint: () => editor?.getNapkinJoint() ?? null,
        onAddNapkinJoint: (options) => editor?.addNapkinJoint(options),
        onFitNapkinJoint: (options) => editor?.fitNapkinJoint(options),
        onRemoveNapkinJoint: () => editor?.removeNapkinJoint(),
        onSetNapkinGuide: (guide) => editor?.setNapkinGuide(guide),
        onAddCrocsPin: (options) => editor?.addCrocsPin(options),
        onUpdateCrocsPin: (pinId, changes) => editor?.updateCrocsPin(pinId, changes),
        onRemoveCrocsPin: (pinId) => editor?.removeCrocsPin(pinId),
        getCrocsPins: () => editor?.getCrocsPins() ?? [],
        hasHoles: () => editor?.hasHoles() ?? false,
        onAddInternalElements: (options) => editor?.addInternalElements(options),
        onUpdateInternalElement: (elementId, changes) => editor?.updateInternalElement(elementId, changes),
        onRemoveInternalElement: (elementId) => editor?.removeInternalElement(elementId),
        getInternalElements: () => editor?.getInternalElements() ?? [],
        // Sin desagrupar: el extractor del backend entiende los `<g>` con id, y
        // aplanar el lienzo sólo tiene sentido cuando lo pide el guardado.
        getDesignSVG: () => editor?.saveSVG() ?? null,
        onGroupObjects: () => editor?.groupActiveObjects(),
        onUngroupObjects: () => editor?.ungroupActiveObjects(),
        onAlignObjects: (edge, referenceMode) => editor?.alignActiveObjects(edge, referenceMode),
        onDistributeObjects: (axis) => editor?.distributeActiveObjects(axis),
        onMirrorObjects: (axis, referenceMode) => editor?.mirrorActiveObjects(axis, referenceMode),
        // Vectorizar texto: dos motores para comparar (ver editor.js).
        // Cliente (opentype.js) es sincrono-en-la-practica; servidor
        // (Inkscape) viaja por red, así que aquí se orquesta el round-trip
        // — editor.js no hace peticiones HTTP, solo toca el canvas.
        onVectorizeTextClient: async () => (await editor?.vectorizeTextClient()) ?? { ok: false, reason: 'no-editor' },
        onVectorizeTextServer: async () => {
            const svg = editor?.getVectorizableTextSvg();
            if (!svg) return { ok: false, reason: 'no-text' };
            try {
                const { data } = await axios.put('customized-keyrings/vectorize-text/', { svg });
                if (!data?.svg) return { ok: false, reason: 'server-error' };
                editor.applyVectorizedTextSvg(data.svg);
                return { ok: true };
            } catch (error) {
                console.error('vectorizeTextServer', error);
                return { ok: false, reason: 'server-error' };
            }
        },
        onUndo: () => editor?.undo(),
        onRedo: () => editor?.redo(),
        onZoomIn: () => editor?.zoomIn(),
        onZoomOut: () => editor?.zoomOut(),
        onResetViewport: (insets) => editor?.resetViewport(insets),
        // Zoom absoluto (1 = 100 %) y sus topes, para el chip del dock de vista.
        getZoom: () => editor?.getZoom() ?? 1,
        onSetZoom: (zoom) => editor?.setZoom(zoom),
        getZoomLimits: () => editor?.getZoomLimits() ?? { min: 0.02, max: 8 },
        // Cotas del fondo: conjunto / por piezas sueltas / ocultas.
        onSetCotaDisplayMode: (mode) => editor?.setCotaDisplayMode(mode),
        getCotaDisplayMode: () => editor?.getCotaDisplayMode() ?? 'conjunto',
        getBackgroundPieces: () => editor?.getBackgroundPieces() ?? [],
        onResizeBackgroundPiece: (index, widthMm, heightMm) => editor?.resizeBackgroundPiece(index, widthMm, heightMm) ?? false,
        takeSnapshot: () => editor?.takeSnapshot(),
        jumpToSnapshot: (index) => editor?.jumpToSnapshot(index),
        historyInfo: historyInfo ?? { entries: [], currentIndex: -1 },
        onSendToBack: () => editor?.sendToBack(),
        onBringForward: () => editor?.bringForward(),
        onSendBackwards: () => editor?.sendBackwards(),
        onBringToFront: () => editor?.bringToFront(),
        canvas: editor?.canvas ?? null,
        getLayerSelectedObjects: () => editor?.getLayerSelectedObjects() ?? [],
        // Visibilidad por objeto/categoría del gestor de capas (Fondo/Agujeros/
        // Fijo/Dibujos) — ver `editor.js` para el porqué del diseño.
        onSetObjectVisible: (fabricObj, visible) => editor?.setObjectVisible(fabricObj, visible),
        onSetObjectRenderExcluded: (fabricObj, excluded) => editor?.setObjectRenderExcluded(fabricObj, excluded),
        isObjectRenderExcluded: (fabricObj) => editor?.isObjectRenderExcluded(fabricObj) ?? false,
        // Relieve por dibujo: cuánto entra y cuánto sobresale, en mm. Sólo
        // llaveros (ver LayerPanel).
        onSetObjectDrawingRelief: (fabricObj, which, mm) => editor?.setObjectDrawingRelief(fabricObj, which, mm),
        getObjectDrawingRelief: (fabricObj) => editor?.getObjectDrawingRelief(fabricObj) ?? {
            depth: { own: null, inherited: null },
            raise: { own: null, inherited: null },
        },
        // Cara del llavero por la que se está pintando (sólo llaveros).
        getEditingFace: () => editor?.getEditingFace() ?? 'front',
        onSetEditingFace: (face) => editor?.setEditingFace(face),
        getObjectDrawingFace: (fabricObj) => editor?.getObjectDrawingFace(fabricObj) ?? 'front',
        onSetObjectDrawingFace: (fabricObj, face) => editor?.setObjectDrawingFace(fabricObj, face),
        onSetFaceVisible: (face, visible) => editor?.setFaceVisible(face, visible),
        onSetCategoryVisible: (category, visible) => editor?.setCategoryVisible(category, visible),
        onSoloCategory: (category) => editor?.soloCategory(category),
        onShowAllObjects: () => editor?.showAllObjects(),
        isObjectVisible: (fabricObj) => editor?.isObjectVisible(fabricObj) ?? true,
        getObjectCategory: (fabricObj) => editor?.getObjectCategory(fabricObj) ?? 'drawings',
        selectObject: (fabricObj, additive) => editor?.selectObject(fabricObj, additive),
        selectNestedObject: (fabricObj, rootGroup, additive) => editor?.selectNestedObject(fabricObj, rootGroup, additive),
        reorderObject: (fromObj, toCanvasIdx) => {
            if (!editor?.canvas) return;
            // No permitir mover las capas fijas (azul) ni el fondo
            if (fromObj?.class === 'drawing_doesnt_move' || String(fromObj?.id ?? '').startsWith('background')) return;
            editor.canvas.moveTo(fromObj, toCanvasIdx);
            // Re-fijar las capas "suelo": fondo abajo del todo y, justo encima,
            // las capas drawing_doesnt_move. Así ningún objeto puede quedar por debajo del azul.
            editor.canvas.getObjects()
                .filter(obj => obj.class === 'drawing_doesnt_move')
                .forEach(obj => editor.canvas.sendToBack(obj));
            editor.canvas.getObjects()
                .filter(obj => String(obj?.id ?? '').startsWith('background'))
                .forEach(obj => editor.canvas.sendToBack(obj));
            editor.canvas.requestRenderAll();
            editor.canvas.fire('layer:reordered');
        },
    };

    return (
        <ToolsContext.Provider value={tools}>
            {children}
        </ToolsContext.Provider>
    );
};

export default ToolProvider;
